import {useApi} from './axios-service';
import {useCustomerSelections} from '@features/Bookings/Services/customer-selection-service';
import {useAuthStore} from "@app/Store/auth-store";
import {getBookingRouteUrl} from "@services/booking-route-service";
import { RouteNames } from "@services/booking-route-service";
import type { LoginResponse } from "@features/Bookings/Views/LoginView/Components/Types/LoginResponse";

export const useAuth = () => {
  const authStore = useAuthStore();
  const {clearCustomerSelections} = useCustomerSelections();
  const logOutApi = useApi('/auth/logout');
  const clearCartApi = useApi('/cart/clear');
  const removeCustomerClubmemberGroupApi = useApi('/sluice/removecustomerclubmembergroup');
  const authMeApi = useApi<LoginResponse>('/auth/me', undefined, { reloadOnAuthError: false });

  const setUser = (payload: User, authenticated: boolean): void => {
    authStore.authState.user = payload;
    authStore.authState.authenticated = authenticated;
  };

  const clearUser = (): void => {
    authStore.authState.user = undefined;
    authStore.authState.authenticated = false;
    authStore.authState.isSluiceHost = false;
    authStore.authState.error = undefined;
  };

  const initializeAuth = async (): Promise<void> => {
    authStore.authState.authenticating = true;

    try {
      const data = await authMeApi.get<LoginResponse>();
      authStore.authState.isSluiceHost = data.isSluiceHost ?? false;
      setUser(createUser(data), true);
    } catch {
      clearUser();
    } finally {
      authStore.authState.authenticating = false;
    }
  };

  const isAuthenticated = (): boolean => {
    return authStore.authState.authenticated;
  };

  const isBusinessCustomer = (): boolean => {
    return authStore.authState.user?.isBusinessCustomer ?? false;
  };

  const isSluiceHost = (): boolean => {
    return authStore.authState.isSluiceHost;
  };

  const clearUserAndPushToLogin = () => {
    clearUser();
    clearCustomerSelections();
    // TODO Kanske behöver cleara proxy customer här
    redirectToLogin();
  };

  const logout = (): void => {
    if (isBusinessCustomer()) {
      clearCartApi.put();
    }
    if (authStore.authState.isSluiceHost) {
      removeCustomerClubmemberGroupApi.put();
    }

    logOutApi.get()
      .then(() => {
        clearUser();
        window.localStorage.setItem('requestVerificationToken', logOutApi.headers.value?.requestverificationtoken);
        redirectToLogin();
      })
      .catch(() => {
        clearUser();
        redirectToLogin();
      })
      .finally(() => {
        clearCustomerSelections();
        // TODO Clear proxy customer here if needed
      });
  };

  const redirectToLogin = (): void => {
    window.location.assign(getBookingRouteUrl(RouteNames.Login));
  };

  const createUser = (data: LoginResponse): User => {
    return {
      access_token: "",
      id: String(data.id ?? data.systemId ?? ""),
      email: data.email ?? "",
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      boats: data.boats ?? [],
      phone: data.phone ?? data.phoneNumber ?? "",
      address: data.address ?? "",
      zipCode: data.zipCode ?? "",
      city: data.city ?? "",
      country: data.country ?? "",
      countryName: data.countryName,
      customerClubName: data.customerClubName,
      isCustomerClubMember: data.isCustomerClubMember ?? false,
      isBusinessCustomer: data.isBusinessCustomer ?? false,
    };
  };

  return {
    setUser,
    initializeAuth,
    isAuthenticated,
    clearUserAndPushToLogin,
    logout,
    isSluiceHost,
    isBusinessCustomer
  };
};
